import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiBriefcase, FiArrowRight, FiLayout, FiUser, FiCheckCircle, FiCheck, FiLink, FiUsers, FiZap } from 'react-icons/fi';
import { settingsService } from '../services/settingsService';
import { useClient } from '../context/ClientContext';
import { industries } from '../data/settingsData';

const plans = [
  { value: 'free', title: 'Free', description: 'Essential tools to get started.' },
  { value: 'starter', title: 'Starter', description: 'For growing businesses.' },
  { value: 'professional', title: 'Professional', description: 'Advanced tools for established businesses.' },
  { value: 'enterprise', title: 'Enterprise', description: 'Scalable controls for complex organizations.' }
];

const positionOptions = [
  'Bookkeeper',
  'Accountant',
  'Senior Accountant',
  'CPA',
  'Tax Accountant',
  'Fractional CFO',
  'Financial Controller',
  'Other'
];

const primaryServiceOptions = [
  'Bookkeeping',
  'Accounting',
  'CPA Services',
  'Tax Preparation',
  'Payroll',
  'Advisory Services'
];

const countryOptions = [
  'United States',
  'Canada',
  'United Kingdom',
  'Australia',
  'Other'
];

const currencyOptions = [
  'USD',
  'CAD',
  'GBP',
  'AUD',
  'EUR',
  'Other'
];

const employeeRangeOptions = [
  '1-5',
  '6-10',
  '11-25',
  '26-50',
  '51-100',
  '101-250',
  '251+'
];

const firmSizeOptions = [
  '1-5',
  '6-10',
  '11-25',
  '26-50',
  '51-100',
  '101+'
];

const specializationOptions = [
  'Small Business',
  'Construction',
  'Healthcare',
  'E-commerce',
  'Real Estate',
  'Nonprofit',
  'Restaurant',
  'Professional Services',
  'Tax',
  'Payroll'
];

const firmIndustryOptions = [
  'Retail',
  'Construction',
  'Healthcare',
  'E-commerce',
  'Real Estate',
  'Professional Services',
  'Restaurant',
  'Nonprofit'
];

const revenueRangeOptions = [
  'Under $100k',
  '$100k-$500k',
  '$500k-$1M',
  '$1M-$5M',
  '$5M+'
];

const welcomeActions = [
  ['Invite Team Members', FiUsers, '/settings/users'],
  ['Invite Clients', FiUsers, '/clients'],
  ['Connect Bank Accounts', FiLink, '/accounting/connect-bank'],
  ['Connect QuickBooks', FiLink, '/dashboard'],
  ['Connect Wave', FiLink, '/dashboard'],
  ['Explore the Platform', FiZap, '/dashboard']
];

export default function Onboarding() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, updateOnboardingStatus } = useClient();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    entity_type: '',
    businessType: '',
    employeeRange: '',
    annualRevenueRange: '',
    bookkeepingProvider: 'Neither',
    position: '',
    specialization: '',
    yearsExperience: '',
    certification: '',
    linkedin: '',
    acceptsClientReferrals: false,
    primaryService: '',
    firmSize: '',
    yearEstablished: '',
    description: '',
    industriesServed: [],
    firstName: '',
    lastName: '',
    country: 'United States',
    currency: 'USD',
    fiscalYear: 'December',
    website: '',
    services: '',
    plan: 'free'
  });

  const isOwner = profile?.role?.toLowerCase() === 'super admin' || profile?.role?.toLowerCase() === 'client owner';
  const userType = profile?.user_type || 'business_owner';
  const isPlatformOwner = userType === 'business_owner';

  useEffect(() => {
    if (profile) {
      setFormData(prev => ({
        ...prev,
        firstName: profile.first_name || '',
        lastName: profile.last_name || ''
      }));
    }
  }, [profile]);

  const entityTypes = [
    "Sole Proprietorship",
    "Partnership",
    "Limited Liability Company (LLC)",
    "C-Corporation",
    "S-Corporation",
    "Non-Profit"
  ];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleFirmIndustry = (industry) => {
    setFormData(prev => ({
      ...prev,
      industriesServed: prev.industriesServed.includes(industry)
        ? prev.industriesServed.filter(item => item !== industry)
        : [...prev.industriesServed, industry]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isPlatformOwner && step === 1) {
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      // Update personal profile too
      await settingsService.updateUserProfile({
        first_name: formData.firstName,
        last_name: formData.lastName
      });

      await settingsService.completePlatformOnboarding({
        ...formData,
        role: userType
      });

      await updateOnboardingStatus(true);
      navigate('/dashboard');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (location.state?.registrationComplete) {
    return <RegistrationWelcome navigate={navigate} />;
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-[-5%] left-[-5%] w-80 h-80 bg-blue-600/10 rounded-full blur-[100px] pointer-events-none"></div>
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl bg-white rounded-[32px] shadow-2xl border border-slate-100 p-8 md:p-12 relative z-10"
      >
        <div className="flex items-center justify-center mb-8">
          <div className="w-16 h-16 bg-slate-900 rounded-2xl flex items-center justify-center shadow-lg">
            <SafeIcon icon={isOwner ? FiLayout : FiUser} className="text-white text-2xl" />
          </div>
        </div>
        
        <div className="text-center mb-10">
          <h1 className="text-3xl font-black text-slate-900 tracking-tight mb-3">
            {isPlatformOwner && step === 1 ? "Choose Your Plan" : isOwner ? "Welcome to ProfitYo" : `Welcome, ${formData.firstName || 'New Member'}`}
          </h1>
          <p className="text-slate-500 font-medium">
            {isPlatformOwner && step === 1
              ? "Select a plan before creating your company workspace."
              : isOwner 
                ? "Let's set up your business profile to personalize your ledger experience."
                : "You've been invited to join the team. Let's quickly confirm your profile details."}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {isPlatformOwner && step === 1 && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {plans.map((plan) => (
                  <button
                    key={plan.value}
                    type="button"
                    onClick={() => setFormData({...formData, plan: plan.value})}
                    className={`relative text-left p-4 rounded-2xl border-2 transition-all ${
                      formData.plan === plan.value
                        ? 'border-emerald-500 bg-emerald-50'
                        : 'border-slate-100 bg-white hover:border-emerald-200'
                    }`}
                  >
                    <p className="text-sm font-black text-slate-900">{plan.title}</p>
                    <p className="text-xs font-bold text-slate-400 mt-1">{plan.description}</p>
                    {formData.plan === plan.value && (
                      <SafeIcon icon={FiCheck} className="absolute right-4 top-4 text-emerald-600" />
                    )}
                  </button>
                ))}
              </div>
              <button
                type="submit"
                className="w-full mt-3 py-5 text-white rounded-[24px] text-xs font-black uppercase tracking-[0.2em] transition-all shadow-xl active:scale-[0.98] flex items-center justify-center gap-3 bg-slate-900 hover:bg-black"
              >
                Continue to Create Company <SafeIcon icon={FiArrowRight} />
              </button>
            </div>
          )}

          {(!isPlatformOwner || step === 2) && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">First Name</label>
                  <input 
                    type="text" 
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({...formData, firstName: e.target.value})}
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-blue-500 focus:bg-white transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Last Name</label>
                  <input 
                    type="text" 
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({...formData, lastName: e.target.value})}
                    className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-blue-500 focus:bg-white transition-all"
                  />
                </div>
              </div>

              {isPlatformOwner && (
                <>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                      <SafeIcon icon={FiBriefcase} /> Business Name
                    </label>
                    <input 
                      type="text" 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                      placeholder="e.g. Acme Corp"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Entity Type</label>
                      <select 
                        required
                        value={formData.entity_type}
                        onChange={(e) => setFormData({...formData, entity_type: e.target.value})}
                        className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                      >
                        <option value="">Select type</option>
                        {entityTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Industry</label>
                      <select 
                        required
                        value={formData.industry}
                        onChange={(e) => setFormData({...formData, industry: e.target.value})}
                        className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                      >
                        <option value="">Select industry</option>
                        {industries.map(ind => (
                          <option key={ind} value={ind}>{ind}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Country *</label>
                      <select
                        required
                        value={formData.country}
                        onChange={(e) => setFormData({...formData, country: e.target.value})}
                        className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                      >
                        <option value="">Select country</option>
                        {countryOptions.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Currency *</label>
                      <select
                        required
                        value={formData.currency}
                        onChange={(e) => setFormData({...formData, currency: e.target.value})}
                        className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                      >
                        <option value="">Select currency</option>
                        {currencyOptions.map(currency => (
                          <option key={currency} value={currency}>{currency}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Employee Range</label>
                    <select
                      value={formData.employeeRange}
                      onChange={(e) => setFormData({...formData, employeeRange: e.target.value})}
                      className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                    >
                      <option value="">Select employee range</option>
                      {employeeRangeOptions.map(range => (
                        <option key={range} value={range}>{range}</option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Annual Revenue Range</label>
                      <select
                        value={formData.annualRevenueRange}
                        onChange={(e) => setFormData({...formData, annualRevenueRange: e.target.value})}
                        className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                      >
                        <option value="">Select revenue range</option>
                        {revenueRangeOptions.map(range => (
                          <option key={range} value={range}>{range}</option>
                        ))}
                      </select>
                    </div>
                    <div className="space-y-2">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Website</label>
                      <input
                        type="url"
                        value={formData.website}
                        onChange={(e) => setFormData({...formData, website: e.target.value})}
                        className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Do You Currently Have</label>
                    <select
                      value={formData.bookkeepingProvider}
                      onChange={(e) => setFormData({...formData, bookkeepingProvider: e.target.value})}
                      className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                    >
                      <option value="Accountant">Accountant</option>
                      <option value="Bookkeeper">Bookkeeper</option>
                      <option value="Neither">Neither</option>
                    </select>
                  </div>
                </>
              )}

              {!isPlatformOwner && (
                <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 flex items-start gap-4">
                  <SafeIcon icon={FiCheckCircle} className="text-blue-500 text-xl shrink-0 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-black text-blue-900 mb-1">Company Access Ready</h4>
                    <p className="text-xs text-blue-700 leading-relaxed font-medium">
                      Your administrator has already configured the company settings. You'll be joining as a <strong>{profile?.role || 'Bookkeeper'}</strong>.
                    </p>
                  </div>
                </div>
              )}

              {!isPlatformOwner && (
                <>
                  <div className="space-y-2">
                    <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">
                      {userType === 'accounting_firm' ? 'Firm Name' : 'Practice Name'}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                    />
                  </div>

                  {userType === 'accounting_firm' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Primary Service *</label>
                        <select
                          required
                          value={formData.primaryService}
                          onChange={(e) => setFormData({...formData, primaryService: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                        >
                          <option value="">Select primary service</option>
                          {primaryServiceOptions.map(service => (
                            <option key={service} value={service}>{service}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Country *</label>
                        <select
                          required
                          value={formData.country}
                          onChange={(e) => setFormData({...formData, country: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                        >
                          <option value="">Select country</option>
                          {countryOptions.map(country => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Website</label>
                        <input
                          type="url"
                          value={formData.website}
                          onChange={(e) => setFormData({...formData, website: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Firm Size</label>
                        <select
                          value={formData.firmSize}
                          onChange={(e) => setFormData({...formData, firmSize: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                        >
                          <option value="">Select firm size</option>
                          {firmSizeOptions.map(size => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Year Established</label>
                        <input
                          type="number"
                          value={formData.yearEstablished}
                          onChange={(e) => setFormData({...formData, yearEstablished: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Company Description</label>
                        <textarea
                          value={formData.description}
                          onChange={(e) => setFormData({...formData, description: e.target.value})}
                          rows="4"
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                        />
                      </div>

                      <div className="space-y-3">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Industries Served</label>
                        <div className="grid grid-cols-2 gap-3">
                          {firmIndustryOptions.map(industry => (
                            <label key={industry} className="flex items-center gap-2 text-xs font-bold text-slate-600">
                              <input
                                type="checkbox"
                                checked={formData.industriesServed.includes(industry)}
                                onChange={() => toggleFirmIndustry(industry)}
                              />
                              {industry}
                            </label>
                          ))}
                        </div>
                      </div>

                      <ReferralChoice
                        value={formData.acceptsClientReferrals}
                        onChange={(value) => updateField('acceptsClientReferrals', value)}
                      />
                    </>
                  )}

                  {userType === 'accountant' && (
                    <>
                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Position *</label>
                        <select
                          required
                          value={formData.position}
                          onChange={(e) => setFormData({...formData, position: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                        >
                          <option value="">Select position</option>
                          {positionOptions.map(position => (
                            <option key={position} value={position}>{position}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Specialization *</label>
                        <select
                          required
                          value={formData.specialization}
                          onChange={(e) => setFormData({...formData, specialization: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                        >
                          <option value="">Select specialization</option>
                          {specializationOptions.map(specialization => (
                            <option key={specialization} value={specialization}>{specialization}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Country *</label>
                        <select
                          required
                          value={formData.country}
                          onChange={(e) => setFormData({...formData, country: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all appearance-none cursor-pointer"
                        >
                          <option value="">Select country</option>
                          {countryOptions.map(country => (
                            <option key={country} value={country}>{country}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Years Experience *</label>
                        <input
                          type="number"
                          min="0"
                          required
                          value={formData.yearsExperience}
                          onChange={(e) => setFormData({...formData, yearsExperience: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Professional Certification</label>
                        <input
                          type="text"
                          value={formData.certification}
                          onChange={(e) => setFormData({...formData, certification: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Website</label>
                        <input
                          type="url"
                          value={formData.website}
                          onChange={(e) => setFormData({...formData, website: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                        />
                      </div>

                      <div className="space-y-2">
                        <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">LinkedIn Profile</label>
                        <input
                          type="url"
                          value={formData.linkedin}
                          onChange={(e) => setFormData({...formData, linkedin: e.target.value})}
                          className="w-full px-5 py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl text-sm font-black outline-none focus:border-emerald-500 focus:bg-white transition-all"
                        />
                      </div>

                      <ReferralChoice
                        value={formData.acceptsClientReferrals}
                        onChange={(value) => updateField('acceptsClientReferrals', value)}
                      />

                      <div className="p-6 bg-blue-50 rounded-3xl border border-blue-100 flex items-start gap-4">
                        <SafeIcon icon={FiCheckCircle} className="text-blue-500 text-xl shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-black text-blue-900 mb-1">Free Accountant Access</h4>
                          <p className="text-xs text-blue-700 leading-relaxed font-medium">
                            Own bookkeeping, client management, reports, invoicing, referrals, and up to 2 team members.
                          </p>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}

              <button 
                disabled={loading}
                className={`w-full mt-8 py-5 text-white rounded-[24px] text-xs font-black uppercase tracking-[0.2em] transition-all shadow-xl active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-70 ${isOwner ? 'bg-slate-900 hover:bg-black' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {loading ? (
                  <div className="w-5 h-5 border-3 border-white/30 border-t-white rounded-full animate-spin"></div>
                ) : (
                  <>{isOwner ? 'Complete Setup' : 'Join Workspace'} <SafeIcon icon={FiArrowRight} /></>
                )}
              </button>
            </>
          )}
        </form>
      </motion.div>
    </div>
  );
}

function RegistrationWelcome({ navigate }) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6 relative overflow-hidden">
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-emerald-500/10 rounded-full blur-[120px]" />
      <div className="absolute bottom-[-5%] left-[-5%] w-80 h-80 bg-blue-600/10 rounded-full blur-[100px]" />
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 w-full max-w-3xl rounded-[32px] border border-slate-100 bg-white p-8 shadow-2xl md:p-12"
      >
        <div className="mb-8 flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-900 shadow-lg">
            <SafeIcon icon={FiBriefcase} className="text-2xl text-white" />
          </div>
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-emerald-600">Workspace ready</p>
            <h1 className="text-3xl font-black tracking-tight text-slate-900">Welcome to ProfitYo</h1>
          </div>
        </div>
        <p className="mb-8 text-sm font-medium leading-relaxed text-slate-500">
          Your workspace and Workspace Owner account are ready. Continue with any recommended action whenever you are ready.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          {welcomeActions.map(([title, Icon, path]) => (
            <button
              type="button"
              key={title}
              onClick={() => navigate(path)}
              className="group flex items-center gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-4 text-left transition hover:border-emerald-200 hover:bg-white hover:shadow-md"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
                <SafeIcon icon={Icon} />
              </span>
              <span className="flex-1 text-sm font-black text-slate-900">{title}</span>
              <SafeIcon icon={FiArrowRight} className="text-slate-300 transition group-hover:text-emerald-600" />
            </button>
          ))}
        </div>
        <div className="mt-8 flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-xs font-bold text-emerald-800">
          <SafeIcon icon={FiCheckCircle} className="shrink-0 text-lg text-emerald-600" />
          The remaining business setup can be completed after you enter the platform.
        </div>
      </motion.div>
    </div>
  );
}

function ReferralChoice({ value, onChange }) {
  return (
    <div className="space-y-2">
      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest">Accept Client Referrals?</label>
      <div className="grid grid-cols-2 gap-3">
        {[true, false].map(option => (
          <button
            type="button"
            key={String(option)}
            onClick={() => onChange(option)}
            className={`py-3 rounded-2xl border-2 text-sm font-black transition-all ${
              value === option
                ? 'border-emerald-500 bg-emerald-50 text-emerald-800'
                : 'border-slate-100 bg-white text-slate-600'
            }`}
          >
            {option ? 'Yes' : 'No'}
          </button>
        ))}
      </div>
    </div>
  );
}